import { ChevronUp, ChevronDown, Loader2, Building2 } from 'lucide-react';
import { useExploreStore } from '../../store/exploreStore';
import { columnsFor } from '../../lib/exploreColumns';
import { ColumnFilterMenu } from './ColumnFilterMenu';
import type { ListEntityKind } from '../../types/list';

function rowIdKind(kind: ListEntityKind, row: any): { id: string; kind: ListEntityKind } {
  if (kind === 'PERSON') return { id: row.person.id, kind: 'PERSON' };
  if (kind === 'PROGRAM') return { id: row.team.id, kind: 'PROGRAM' };
  return { id: row.organization.id, kind: row.organization.type }; // ORG rows carry their REAL type (SCHOOL/CLUB_TEAM)
}

function fmt(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (v instanceof Date || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v))) {
    const d = new Date(v as any);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    return days <= 0 ? 'today' : days < 30 ? `${days}d ago` : days < 365 ? `${Math.floor(days / 30)}mo ago` : `${Math.floor(days / 365)}y ago`;
  }
  return String(v);
}

/** Phone card: show Name + next N cols as label:value lines */
function PhoneCard({
  kind,
  row,
  cols,
  isSelected,
  onToggle,
}: {
  kind: ListEntityKind;
  row: any;
  cols: ReturnType<typeof columnsFor>;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const { id } = rowIdKind(kind, row);
  const name = cols[0] ? fmt(cols[0].render(row)) : '—';
  const details = cols.slice(1, 5); // up to 4 extra fields

  return (
    <div
      className={`card p-3 flex gap-3 items-start transition-colors ${
        isSelected
          ? 'border border-[#b7ee41] dark:border-[#b7ee41]'
          : 'border border-transparent'
      }`}
    >
      <input
        type="checkbox"
        aria-label={`Select ${name || 'row'}`}
        checked={isSelected}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#b7ee41] cursor-pointer"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{name}</p>
        <dl className="mt-1 space-y-0.5">
          {details.map((col) => {
            const rawVal = fmt(col.render(row));
            if (rawVal === '—' && !col.cell) return null;
            const cellContent = col.cell ? col.cell(row) : rawVal;
            // Skip null/empty cell output
            if (cellContent == null) return null;
            return (
              <div key={col.key} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <dt className="font-medium text-gray-400 dark:text-gray-500 shrink-0">{col.label}:</dt>
                <dd className="truncate text-gray-700 dark:text-gray-300">{cellContent}</dd>
              </div>
            );
          })}
        </dl>
      </div>
      {/* row id for a11y */}
      <span className="sr-only">{id}</span>
    </div>
  );
}

/** Pagination footer shared by both tiers */
function PaginationFooter({
  page,
  pagination,
  setPage,
}: {
  page: number;
  pagination: { page: number; limit: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean } | null;
  setPage: (p: number) => void;
}) {
  if (!pagination || pagination.totalCount === 0) return null;

  const from = (page - 1) * pagination.limit + 1;
  const to = Math.min(page * pagination.limit, pagination.totalCount);

  return (
    <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
      <span>
        Showing {from.toLocaleString()}–{to.toLocaleString()} of {pagination.totalCount.toLocaleString()}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-secondary text-xs py-1 px-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!pagination.hasPrevPage}
          onClick={() => setPage(page - 1)}
        >
          ← Prev
        </button>
        <button
          type="button"
          className="btn-secondary text-xs py-1 px-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!pagination.hasNextPage}
          onClick={() => setPage(page + 1)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export function ExploreGrid() {
  const {
    kind,
    rows,
    pagination,
    visibleColumns,
    sort,
    selected,
    filterSpec,
    isLoading,
    error,
    sportOptions,
    setSort,
    setFilter,
    toggleSelect,
    selectAllOnPage,
    setPage,
    page,
  } = useExploreStore();

  const cols = columnsFor(kind).filter((c) => visibleColumns.includes(c.key));

  // Defensive: only render rows whose shape matches the current kind. The row
  // renderers read kind-specific relations (row.person / row.team / row.organization),
  // so a transient kind/rows mismatch (e.g. mid-tab-switch) must never reach them.
  const safeRows = rows.filter((r) =>
    kind === 'PERSON' ? r?.person : kind === 'PROGRAM' ? r?.team : r?.organization,
  );

  // ---- Sort toggle ----
  const handleSort = (sortField: string) => {
    if (sort?.field === sortField) {
      setSort({ field: sortField, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ field: sortField, dir: 'asc' });
    }
  };

  // ---- Select-all state for current page ----
  const pageKeys = safeRows.map((r) => rowIdKind(kind, r));
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k.id));

  // ---- Loading / error / empty states ----
  // Only show the full-screen spinner on the INITIAL empty load.
  // On refetches with existing rows, keep the table mounted (so in-table filter
  // popovers and text inputs survive) and dim only the tbody.
  if (isLoading && safeRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-400 dark:text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-500 dark:text-gray-400">
        <span className="text-sm">Couldn't load results. Try adjusting your filters or reloading.</span>
      </div>
    );
  }

  return (
    <div>
      {/* ── DESKTOP / TABLET TABLE ── */}
      <div className="hidden md:block">
        {/* Card surface wrapping table + pagination */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              {/* ── Header row 1: labels / sort ── */}
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {/* Select-all checkbox — sticky left */}
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-3 py-2 text-left w-8"
                  >
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      checked={allPageSelected}
                      onChange={() => selectAllOnPage(pageKeys)}
                      className="h-4 w-4 accent-[#b7ee41] cursor-pointer"
                    />
                  </th>

                  {cols.map((col, idx) => {
                    const isFirst = idx === 0;
                    const isActive = sort?.field === col.sortField;

                    const thClass = [
                      'px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 whitespace-nowrap',
                      isFirst ? 'sticky z-10 bg-white dark:bg-gray-800' : '',
                      isFirst ? 'left-8' : '',
                    ].join(' ');

                    if (col.sortField) {
                      return (
                        <th key={col.key} scope="col" className={thClass}>
                          <button
                            type="button"
                            onClick={() => handleSort(col.sortField!)}
                            className="flex items-center gap-0.5 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                          >
                            {col.label}
                            {isActive ? (
                              sort?.dir === 'asc' ? (
                                <ChevronUp className="h-3 w-3 text-[#b7ee41]" />
                              ) : (
                                <ChevronDown className="h-3 w-3 text-[#b7ee41]" />
                              )
                            ) : (
                              <ChevronDown className="h-3 w-3 opacity-30" />
                            )}
                          </button>
                        </th>
                      );
                    }

                    return (
                      <th key={col.key} scope="col" className={thClass}>
                        {col.label}
                      </th>
                    );
                  })}
                </tr>

                {/* ── Header row 2: inline filters ── */}
                <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/50">
                  {/* Empty cell for checkbox column */}
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-gray-50/60 dark:bg-gray-800/50 px-3 py-1 w-8"
                  />
                  {cols.map((col, idx) => {
                    const isFirst = idx === 0;
                    const thClass = [
                      'px-3 py-1',
                      isFirst ? 'sticky z-10 bg-gray-50/60 dark:bg-gray-800/50 left-8' : '',
                    ].join(' ');

                    // For the Programs Sport column, inject the dynamically-fetched sport options.
                    const filterControl =
                      col.filter?.type === 'enum' && col.filter.axis === 'sports'
                        ? { ...col.filter, options: sportOptions }
                        : col.filter;

                    return (
                      <th key={col.key} scope="col" className={thClass}>
                        {filterControl ? (
                          <ColumnFilterMenu
                            control={filterControl}
                            spec={filterSpec}
                            onChange={setFilter}
                            label={col.label}
                          />
                        ) : null}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* ── Body ── */}
              <tbody className={isLoading ? 'opacity-60 transition-opacity' : undefined}>
                {safeRows.length === 0 ? (
                  <tr>
                    <td colSpan={cols.length + 1} className="px-3 py-12 text-center text-sm text-gray-400 dark:text-gray-500">
                      No matches — adjust or clear your filters above.
                    </td>
                  </tr>
                ) : (
                  safeRows.map((row) => {
                  const { id, kind: rowKind } = rowIdKind(kind, row);
                  const isSelected = selected.has(id);
                  const rowName = cols[0] ? fmt(cols[0].render(row)) : '';

                  const stickyBg = isSelected
                    ? 'bg-[#f7fee7] dark:bg-[#1a2e05]'
                    : 'bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/30';

                  return (
                    <tr
                      key={id}
                      className={[
                        'group border-b border-gray-100 dark:border-gray-700/60',
                        'hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors',
                        isSelected ? 'bg-[#f7fee7] dark:bg-[#1a2e05]' : '',
                      ].join(' ')}
                    >
                      {/* Per-row checkbox — sticky left */}
                      <td className={`sticky left-0 z-10 px-3 py-2 w-8 ${stickyBg}`}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${rowName || 'row'}`}
                          checked={isSelected}
                          onChange={() => toggleSelect(id, rowKind)}
                          className="h-4 w-4 accent-[#b7ee41] cursor-pointer"
                        />
                      </td>

                      {cols.map((col, idx) => {
                        const isFirst = idx === 0;
                        const tdClass = [
                          'px-3 py-2 text-sm text-gray-700 dark:text-gray-300',
                          isFirst
                            ? `sticky left-8 z-10 ${stickyBg} font-medium text-gray-900 dark:text-gray-100 max-w-[14rem] truncate`
                            : 'whitespace-nowrap max-w-[12rem] truncate',
                        ].join(' ');

                        // First column (name): show a leading org icon for ORG rows
                        if (isFirst && kind === 'ORG') {
                          return (
                            <td key={col.key} className={tdClass} title={fmt(col.render(row))}>
                              <span className="inline-flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-purple-400 dark:text-purple-500 flex-shrink-0" />
                                <span className="truncate">{fmt(col.render(row))}</span>
                              </span>
                            </td>
                          );
                        }

                        return (
                          <td key={col.key} className={tdClass} title={fmt(col.render(row))}>
                            {col.cell ? col.cell(row) : fmt(col.render(row))}
                          </td>
                        );
                      })}
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Desktop pagination — inside the card */}
          <PaginationFooter page={page} pagination={pagination} setPage={setPage} />
        </div>
      </div>

      {/* ── PHONE CARDS ── */}
      <div className="md:hidden">
        <div className={`flex flex-col gap-2 p-3${isLoading ? ' opacity-60 transition-opacity' : ''}`}>
          {safeRows.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
              No matches — adjust or clear your filters above.
            </div>
          ) : (
            safeRows.map((row) => {
              const { id, kind: rowKind } = rowIdKind(kind, row);
              return (
                <PhoneCard
                  key={id}
                  kind={kind}
                  row={row}
                  cols={cols}
                  isSelected={selected.has(id)}
                  onToggle={() => toggleSelect(id, rowKind)}
                />
              );
            })
          )}
        </div>

        {/* Phone pagination */}
        <PaginationFooter page={page} pagination={pagination} setPage={setPage} />
      </div>
    </div>
  );
}
